import { readonly, validateStandardSchema } from '@angular/forms/signals';

import type { Side, TabEntity } from '@sites/entity-core';
import { createCorners, createSpacing, DefaultTabEntitySchema } from '@sites/entity-core';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { AdvancedRadiusSettings } from '@components/sp-advanced-radius-settings/type/sp-advanced-radius-settings.type';

import type { BaseEntityFormOptions } from '../../core/create-base-entity-form';
import { createBaseEntityForm } from '../../core/create-base-entity-form';
import { mergeAccessors } from '../../core/merge-accessors';
import { createAnimationAccessors, applyAnimationFieldRules } from '../../features/animation/animation-accessors';
import { withSpacingSides } from '../../mixins/spacing/spacing-updaters';

type ContourSide = 'top' | 'left' | 'right' | 'bottom';

type TabData = TabEntity['props'];

export type TabFormModel = TabEntity;
export type TabDataModel = TabFormModel['props'];

export type TabForm = ReturnType<typeof createTabForm>;

/** A picked preset is stored by name, so the colour keeps following the theme. */
const toColorValue = ({ value, presetName }: ColorPickerEventPayload): string | null => presetName ?? value ?? null;

export function createTabForm(options?: BaseEntityFormOptions<TabEntity>) {
    const base = createBaseEntityForm<TabEntity>((schemaPath) => {
        validateStandardSchema(schemaPath, DefaultTabEntitySchema);
        readonly(schemaPath.id);
        readonly(schemaPath.entityType);
        readonly(schemaPath.entitySubType);
        readonly(schemaPath.version);
        // readonly(schemaPath.children);
        // readonly(schemaPath.parentId);
        applyAnimationFieldRules(schemaPath.props.animation);
    }, options);

    const { _raw } = base;

    const formApi = {
        ...base,

        get header() {
            return _raw.props.header;
        },
        get body() {
            return _raw.props.body;
        },

        // Root — children
        get children() {
            return _raw.children;
        },
        updateChildren: (updater: (current: string[]) => string[]) => {
            _raw.children().value.update((current) => updater(current as string[]));
        },

        // Placement
        get placement() {
            return _raw.props.placement;
        },
        setPlacement: (value: TabData['placement']) => {
            _raw.props.placement().value.set(value);
        },

        // Tabs
        get tabs() {
            return _raw.props.tabs;
        },
        setTabs: (value: TabData['tabs']) => {
            _raw.props.tabs().value.set(value);
        },
        updateTabs: (updater: (current: TabData['tabs']) => TabData['tabs']) => {
            _raw.props.tabs().value.update(updater);
        },
        tabTitle: (index: number) => _raw.props.tabs[index].title,

        // Header — background color
        get headerBackgroundColor() {
            return _raw.props.header.backgroundColor.desktop;
        },
        setHeaderBackgroundColor: (payload: ColorPickerEventPayload) => {
            _raw.props.header.backgroundColor.desktop().value.set(toColorValue(payload));
        },

        // Header — active background color
        get headerActiveBackgroundColor() {
            return _raw.props.header.activeBackgroundColor.desktop;
        },
        setHeaderActiveBackgroundColor: (payload: ColorPickerEventPayload) => {
            _raw.props.header.activeBackgroundColor.desktop().value.set(toColorValue(payload));
        },

        // Header — text color
        get headerTextColor() {
            return _raw.props.header.textColor.desktop;
        },
        setHeaderTextColor: (payload: ColorPickerEventPayload) => {
            _raw.props.header.textColor.desktop().value.set(toColorValue(payload));
        },

        // Header — active text color
        get headerActiveTextColor() {
            return _raw.props.header.activeTextColor.desktop;
        },
        setHeaderActiveTextColor: (payload: ColorPickerEventPayload) => {
            _raw.props.header.activeTextColor.desktop().value.set(toColorValue(payload));
        },

        // Header — font size (full {value, unit})
        get headerFontSize() {
            return _raw.props.header.fontSize.desktop;
        },
        // Header — font size (inner value only)
        get headerFontSizeValue() {
            return _raw.props.header.fontSize.desktop.value;
        },
        // Header — font size (unit only)
        get headerFontSizeUnit() {
            return _raw.props.header.fontSize.desktop.unit;
        },
        setHeaderFontSize: (value: number) => {
            _raw.props.header.fontSize.desktop().value.set({ value, unit: 'px' });
        },

        // Header — font weight
        get headerFontWeight() {
            return _raw.props.header.fontWeight.desktop;
        },
        toggleHeaderFontWeight: () => {
            const current = _raw.props.header.fontWeight.desktop().value();
            _raw.props.header.fontWeight.desktop().value.set(current === 'initial' ? 'bold' : 'initial');
        },

        // Header — font style
        get headerFontStyle() {
            return _raw.props.header.fontStyle.desktop;
        },
        toggleHeaderFontStyle: () => {
            const current = _raw.props.header.fontStyle.desktop().value();
            _raw.props.header.fontStyle.desktop().value.set(current === 'initial' ? 'italic' : 'initial');
        },

        // Header — text transform
        get headerTextTransform() {
            return _raw.props.header.textTransform.desktop;
        },
        toggleHeaderTextTransform: () => {
            const current = _raw.props.header.textTransform.desktop().value();
            _raw.props.header.textTransform.desktop().value.set(current === 'initial' ? 'uppercase' : 'initial');
        },

        // Header — active contour side
        get headerActiveContourSide() {
            return _raw.props.header.activeContourSide.desktop;
        },
        setHeaderActiveContourSide: (value: ContourSide | null) => {
            _raw.props.header.activeContourSide.desktop().value.set(value);
        },
        toggleHeaderActiveContourSide: (value: ContourSide) => {
            const current = _raw.props.header.activeContourSide.desktop().value();
            _raw.props.header.activeContourSide.desktop().value.set(current === value ? null : value);
        },

        // Header — active contour color
        get headerActiveContourColor() {
            return _raw.props.header.activeContourColor.desktop;
        },
        setHeaderActiveContourColor: (payload: ColorPickerEventPayload) => {
            _raw.props.header.activeContourColor.desktop().value.set(toColorValue(payload));
        },

        // Header — separator
        get headerSeparator() {
            return _raw.props.header.separator.desktop;
        },
        setHeaderSeparator: (value: boolean) => {
            _raw.props.header.separator.desktop().value.set(value);
        },

        // Header — separator color
        get headerSeparatorColor() {
            return _raw.props.header.separatorColor.desktop;
        },
        setHeaderSeparatorColor: (payload: ColorPickerEventPayload) => {
            _raw.props.header.separatorColor.desktop().value.set(toColorValue(payload));
        },

        // Header — border radius (full {value, unit})
        get headerBorderRadius() {
            return _raw.props.header.borderRadius.desktop;
        },
        // Header — border radius (inner value only)
        get headerBorderRadiusValue() {
            return _raw.props.header.borderRadius.desktop.value;
        },
        // Header — border radius (unit only)
        get headerBorderRadiusUnit() {
            return _raw.props.header.borderRadius.desktop.unit;
        },
        setAllHeaderBorderRadius: (value: number) => {
            _raw.props.header.borderRadius.desktop.value().value.set(createCorners(value).value);
        },
        setHeaderBorderRadius: (v: AdvancedRadiusSettings) => {
            _raw.props.header.borderRadius.desktop.value().value.set({
                topLeft: v.borderTopLeftRadius,
                topRight: v.borderTopRightRadius,
                bottomLeft: v.borderBottomLeftRadius,
                bottomRight: v.borderBottomRightRadius,
            });
        },

        // Header — padding (full {value, unit})
        get headerPadding() {
            return _raw.props.header.padding.desktop;
        },
        // Header — padding (inner value only)
        get headerPaddingValue() {
            return _raw.props.header.padding.desktop.value;
        },
        // Header — padding (unit only)
        get headerPaddingUnit() {
            return _raw.props.header.padding.desktop.unit;
        },
        PADDINGS_MAP: {
            s: { left: 14, right: 14, top: 8, bottom: 8 },
            m: { left: 18, right: 18, top: 12, bottom: 12 },
            l: { left: 22, right: 22, top: 16, bottom: 16 },
        } as const,
        setHeaderPaddingPreset: (size: 's' | 'm' | 'l') => {
            const map = {
                s: { left: 14, right: 14, top: 8, bottom: 8 },
                m: { left: 18, right: 18, top: 12, bottom: 12 },
                l: { left: 22, right: 22, top: 16, bottom: 16 },
            };
            _raw.props.header.padding.desktop().value.set({ value: map[size], unit: 'px' });
        },

        // Header - width (full {value, unit})
        get headerWidth() {
            return _raw.props.header.width.desktop;
        },
        // Header - width (inner value only)
        get headerWidthValue() {
            return _raw.props.header.width.desktop.value;
        },
        // Header - width (unit only)
        get headerWidthUnit() {
            return _raw.props.header.width.desktop.unit;
        },
        setHeaderWidth: (value: number) => {
            _raw.props.header.width.desktop().value.set({ value, unit: 'px' });
        },

        // Body — background color
        get bodyBackgroundColor() {
            return _raw.props.body.backgroundColor.desktop;
        },
        setBodyBackgroundColor: (payload: ColorPickerEventPayload) => {
            _raw.props.body.backgroundColor.desktop().value.set(toColorValue(payload));
        },

        // Body — border radius (full {value, unit})
        get bodyBorderRadius() {
            return _raw.props.body.borderRadius.desktop;
        },
        // Body — border radius (inner value only)
        get bodyBorderRadiusValue() {
            return _raw.props.body.borderRadius.desktop.value;
        },
        // Body — border radius (unit only)
        get bodyBorderRadiusUnit() {
            return _raw.props.body.borderRadius.desktop.unit;
        },
        setAllBodyBorderRadius: (value: number) => {
            _raw.props.body.borderRadius.desktop().value.set(createCorners(value));
        },

        // Body — padding (full {value, unit})
        get bodyPadding() {
            return _raw.props.body.padding.desktop;
        },
        // Body — padding (inner value only)
        get bodyPaddingValue() {
            return _raw.props.body.padding.desktop.value;
        },
        // Body — padding (unit only)
        get bodyPaddingUnit() {
            return _raw.props.body.padding.desktop.unit;
        },
        setAllBodyPaddings: (value: number) => {
            _raw.props.body.padding.desktop().value.set(createSpacing(value));
        },
        setBodyPaddingSide: (side: Side, value: number) => {
            _raw.props.body.padding.desktop().value.update(withSpacingSides({ [side]: value }));
        },
    };

    // Animation (non-responsive) — shared feature accessors
    return mergeAccessors(
        formApi,
        createAnimationAccessors(() => _raw.props.animation)
    );
}
